import { Empty } from '@byecode/ui'
import { getAssetUrl } from '@lighthouse/assets'
import { AnimationDecorators, useContainerBlockContext, useCustomViewBlockContext, ViewBlockProvider } from '@lighthouse/block'
import { CustomTreeBlock } from '@lighthouse/block/src/blocks'
import type {
    ActorEvent,
    CustomViewConfig,
    RecordLikeProtocol,
    ViewControlHorizontalScrollToAction,
    ViewControlScrollToRecordAction
} from '@lighthouse/core'
import { DataSourceType } from '@lighthouse/core'
import {
    CenteredWrapper,
    findRecordInPool,
    getCursorStyle,
    getPaddingStyle,
    getPrimaryDsSystemFieldId,
    getWithScopeIdAndRecordIdAndColumnId,
    scroll2EndLeftOrRight,
    scroll2ViewItem,
    scrollToRecord,
    useAppContainerContext,
    useAtomAsyncAction,
    useHandleAbortPrevious,
    useRegisterViewControlListeners
} from '@lighthouse/shared'
import { useBreakpoint } from '@lighthouse/tools'
import { useStore } from 'jotai'
import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'

import { fetchViewRecordsAtom } from '@/atoms/blockRecordsDict/action'
import { updateRecordAtom } from '@/atoms/dataSource/action'
import { recordPoolAtom } from '@/atoms/dataSource/state'
import { useCurrentPageContext, useCurrentStackIdContext } from '@/contexts/PageContext'
import { useActionTrigger } from '@/hooks/useActionTrigger'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'

import { useConfigure } from '../hooks/useConfigure'
import { useFetchViewDataDeps } from '../hooks/useFetchViewDataDeps'
import { useFilter } from '../hooks/useFilter'
import { useLinkFilter } from '../hooks/useLinkFilter'
import { useRenderRichText } from '../hooks/useRenderRichText'
import { useSubscribeView } from '../hooks/useSubscribeView'
import { useViewIndependent } from '../hooks/useViewIndependent'
import type { CustomControllerProps, FetchViewDataPayload } from '../types'

const EmptyImage = styled.img`
    width: 140px;
    height: 144px;
`

const SCxViewContainer = styled.div`
    display: flex;
    flex-direction: column;
    padding: 1px;
`

const CustomTreeController: React.FC<CustomControllerProps> = ({
    blockData,
    node,
    firstRef,
    onBlockChange,
    onReady,
    children,
    ...rest
}) => {
    const { id, config, title } = blockData
    const {
        pointer,
        viewType,
        canSearch,
        breakPoint: baseBreakpoint,
        filter,
        sorts,
        cursor,
        linkFilterController,
        treeConfig,
        canDragTreeSort
    } = config as CustomViewConfig

    const { scale } = useAppContainerContext()

    const envId = useCurrentEnvId()
    const stackId = useCurrentStackIdContext()
    const { pageId } = useCurrentPageContext()
    const { scope } = useContainerBlockContext()
    const { record } = useCustomViewBlockContext()
    const uniqueId = getWithScopeIdAndRecordIdAndColumnId(id, scope, record?.id)

    const { handleActionListTrigger } = useActionTrigger(id)
    const appId = useCurrentAppId()
    const dataSourceList = useDataSourceList(appId, envId)
    const dataSource = useDataSource(appId, envId, pointer)

    const { run: updateRecord } = useAtomAsyncAction(updateRecordAtom)

    // 视图独立数据hook
    const { recordPoints, currentPage } = useViewIndependent({
        blockId: uniqueId,
        pointer,
        canSearch
    })

    const handleRenderRichText = useRenderRichText(baseBreakpoint.emptyView?.title)

    const { loading, value: fetchViewRecordsValue, run: fetchViewRecords } = useAtomAsyncAction(fetchViewRecordsAtom)

    // 首批数据加载完成且渲染完成后调用 onReady
    const hasCalledReady = useRef(false)
    useEffect(() => {
        if (!loading && fetchViewRecordsValue && !hasCalledReady.current) {
            // 双重 requestAnimationFrame 确保 DOM 渲染完成
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    hasCalledReady.current = true
                    onReady?.()
                })
            })
        }
    }, [loading, onReady, fetchViewRecordsValue])

    const { mutation } = useHandleAbortPrevious(fetchViewRecords)
    const { ref, breakPoint } = useBreakpoint([!!fetchViewRecordsValue], { scale })

    // 筛选器hook
    const { resolvedFilter } = useFilter({
        filter,
        dataSourceList
    })

    // 联动筛选器hook
    const { resolvedLinkFilter } = useLinkFilter({
        appId,
        envId,
        pageId,
        linkFilterController
    })

    // 互斥逻辑：customTreeSortMap优先，启用拖拽排序时禁用sorts配置
    const hasCustomTreeSort = !!canDragTreeSort && !!treeConfig?.sortId
    const effectiveSorts = hasCustomTreeSort ? undefined : sorts

    // 排序hook
    // const { sortCache, mergeSort } = useSort({
    //     appId,
    //     envId,
    //     blockId: uniqueId,
    //     canSort,
    //     sorts: effectiveSorts
    // })

    // 使用新的依赖管理方案，优化重新渲染（仅包含对象/数组类型字段）
    const stableDeps = useFetchViewDataDeps({
        resolvedFilter,
        resolvedLinkFilter,
        sorts: effectiveSorts
    })

    const fetchViewData = useCallback(
        async (params: FetchViewDataPayload) => {
            const { pageNum } = params
            if (pointer) {
                await mutation({
                    stackId,
                    pageId,
                    viewId: id,
                    uniqueId,
                    scope,
                    isSyncComponent: !!scope,
                    pagination: { currentPage: pageNum, pageSize: 10000 },
                    params: {
                        filter: stableDeps.resolvedFilter,
                        linkFilter: stableDeps.resolvedLinkFilter,
                        sorts: stableDeps.sorts
                    },
                    disableFetchCount: true
                })
            }
        },
        [
            pointer,
            mutation,
            stackId,
            pageId,
            id,
            uniqueId,
            scope,
            stableDeps.resolvedFilter,
            stableDeps.resolvedLinkFilter,
            stableDeps.sorts
        ]
    )

    const handleRecordClickEvent = useCallback(
        (id: string, actorEvent: ActorEvent, record?: RecordLikeProtocol) =>
            handleActionListTrigger(id, actorEvent, { viewRecord: record }),
        [handleActionListTrigger]
    )

    const handleRecordUpdate = useCallback(
        (recordId: string, content: RecordLikeProtocol['content']) => {
            return updateRecord({ envId, dsId: pointer, id: recordId, content })
        },
        [envId, pointer, updateRecord]
    )

    useConfigure({
        blockData,
        fetchViewData
    })

    // 接受其他命令执行加载数据操作
    useSubscribeView({ pointer, fetchViewData, pageNum: currentPage })

    const handleViewRefresh = useCallback(() => {
        fetchViewData({ pageNum: 1 })
    }, [fetchViewData])

    const idFieldId = useMemo(
        () =>
            (dataSource?.type === DataSourceType.joinDataSource
                ? getPrimaryDsSystemFieldId('ID', dataSource.schema, dataSource.viewOptions, dataSource.type)
                : 'ID') ?? 'ID',
        [dataSource?.schema, dataSource?.type, dataSource?.viewOptions]
    )

    const store = useStore()

    // 扔到上层
    useRegisterViewControlListeners(id, scope, 'view', {
        refresh: handleViewRefresh,
        horizontalScrollTo: (payload: ViewControlHorizontalScrollToAction['params'] | undefined) => {
            const to = payload?.horizontalScrollTo
            const horizontalScrollDirection = payload?.horizontalScrollDirection
            const viewContainer = ref.current
            const items = viewContainer?.querySelectorAll('[data-view-item]')
            const viewItemsWrapper = items?.[0]?.parentElement
            if (!to || !items || !viewItemsWrapper) {
                return
            }
            const viewContentContainer = viewItemsWrapper?.parentElement
            const currentScrollLeft = viewItemsWrapper?.parentElement?.scrollLeft ?? 0
            const itemOffsetWidth = items[1].getBoundingClientRect().left - items[0].getBoundingClientRect().left
            const currentIndex = Math.round(currentScrollLeft / itemOffsetWidth)

            let newIndex

            if (!Number.isNaN(Number(to))) {
                newIndex = horizontalScrollDirection === 'left' ? currentIndex - Number(to) : currentIndex + Number(to)
            }

            if (to === 'end') {
                viewContentContainer &&
                    (horizontalScrollDirection === 'left'
                        ? scroll2EndLeftOrRight(viewContentContainer, { left: 0, behavior: 'smooth' })
                        : scroll2EndLeftOrRight(viewContentContainer, { left: viewItemsWrapper.clientWidth, behavior: 'smooth' }))
            }

            if (newIndex === undefined) {
                return
            }
            scroll2ViewItem({ id, scope }, newIndex, { block: 'nearest', inline: 'nearest' })
        },
        scrollToRecord: (payload: ViewControlScrollToRecordAction<string>['params'] | undefined) => {
            const recordNumber = payload?.scrollToRecord
            if (!recordNumber) {
                return
            }

            const allRecords = store.get(recordPoolAtom)
            const viewRecords = recordPoints.reduce<RecordLikeProtocol[]>((acc, item) => {
                const record = findRecordInPool(allRecords, pointer, item.id)
                if (record) {
                    acc.push(record)
                }
                return acc
            }, [])
            const record = viewRecords.find(item => item.content['ID']?.value === recordNumber)
            const recordId = record?.id

            if (recordId) {
                scrollToRecord({ id, scope }, recordId)
            }
        }
    })

    return useMemo(() => {
        if (!pointer || !dataSource) {
            const emptyImageUrl = getAssetUrl('empty', 'no_datasource.svg')
            return (
                <CenteredWrapper style={{ width: '100%', height: '100%', overflow: baseBreakpoint.size.overflow }}>
                    <Empty
                        style={{ margin: '32px 0' }}
                        icon={<EmptyImage src={emptyImageUrl} alt="请在「右侧栏>配置」中选择数据来源" />}
                        description="请在「右侧栏>配置」中选择数据来源"
                    />
                </CenteredWrapper>
            )
        }

        // 首次加载未完成时（包括请求未开始和请求进行中），不渲染内容，避免闪现"暂无记录"
        if (firstRef.current && !fetchViewRecordsValue) {
            return null
        }
        // 首次加载完成，标记为非首次
        if (firstRef.current && fetchViewRecordsValue) {
            firstRef.current = false
        }

        return (
            <AnimationDecorators
                blockId={id}
                blockName={title}
                data-block-type={viewType}
                data-stop-action-propagation
                animation={baseBreakpoint.animation}
                {...rest}
                style={{
                    width: '100%',
                    height: '100%',
                    overflow: baseBreakpoint.size.overflow,
                    ...rest.style,
                    ...(viewType === 'custom'
                        ? { ...getPaddingStyle(baseBreakpoint.viewLayout?.viewPadding, true), ...getCursorStyle(appId, cursor) }
                        : {})
                }}
            >
                <SCxViewContainer ref={ref}>
                    {breakPoint ? (
                        <ViewBlockProvider
                            uniqueId={uniqueId}
                            blockData={blockData}
                            dataSource={dataSource}
                            recordPoints={recordPoints}
                            recordPoolAtom={recordPoolAtom}
                            dataSourceList={dataSourceList}
                        >
                            <CustomTreeBlock
                                node={node}
                                breakPoint={breakPoint}
                                onBlockChange={onBlockChange}
                                onRecordClick={handleRecordClickEvent}
                                onUpdateRecord={handleRecordUpdate}
                                onRenderRichText={handleRenderRichText}
                            />
                        </ViewBlockProvider>
                    ) : null}
                </SCxViewContainer>
                {children}
            </AnimationDecorators>
        )
    }, [
        pointer,
        dataSource,
        firstRef,
        fetchViewRecordsValue,
        recordPoints,
        id,
        title,
        viewType,
        baseBreakpoint.animation,
        baseBreakpoint.size.overflow,
        baseBreakpoint.viewLayout?.viewPadding,
        rest,
        appId,
        cursor,
        ref,
        breakPoint,
        uniqueId,
        blockData,
        dataSourceList,
        node,
        onBlockChange,
        handleRecordClickEvent,
        handleRecordUpdate,
        handleRenderRichText,
        children
    ])
}

export default CustomTreeController
